import { useReducer } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateCrmMaster } from "@/lib/api/crm/masters";
import { CreateCrmMasterRequestValidation } from "@/lib/validation/crm";
import { RoutePath } from "../../routerPaths";
import { crmValidationIssues, isCrmAuthError, type CrmValidationIssue } from "../crmErrors";

type NameField = "long_name" | "short_name";

type CreateGroupState = {
  longName: string;
  shortName: string;
  /** Short name follows Name until it's typed in. */
  shortNameEdited: boolean;
  fieldErrors: Partial<Record<NameField, string>>;
  error: string | null;
};

type CreateGroupAction =
  | { type: "changeLongName"; longName: string }
  | { type: "changeShortName"; shortName: string }
  | { type: "createFailed"; fieldErrors: Partial<Record<NameField, string>>; error: string | null };

const initialCreateGroupState: CreateGroupState = {
  longName: "",
  shortName: "",
  shortNameEdited: false,
  fieldErrors: {},
  error: null,
};

function createGroupReducer(state: CreateGroupState, action: CreateGroupAction): CreateGroupState {
  switch (action.type) {
    case "changeLongName":
      return {
        ...state,
        longName: action.longName,
        shortName: state.shortNameEdited ? state.shortName : action.longName,
        fieldErrors: {},
        error: null,
      };
    case "changeShortName":
      return { ...state, shortName: action.shortName, shortNameEdited: action.shortName !== "", fieldErrors: {}, error: null };
    case "createFailed":
      return { ...state, fieldErrors: action.fieldErrors, error: action.error };
  }
}

/** Issues on the name fields go under them; anything else goes to the banner. */
function toNameErrors(issues: CrmValidationIssue[]): { fieldErrors: Partial<Record<NameField, string>>; error: string | null } {
  const fieldErrors: Partial<Record<NameField, string>> = {};
  const other: string[] = [];
  for (const issue of issues) {
    const field = issue.path[0];
    if (field === "long_name" || field === "short_name") {
      fieldErrors[field] = issue.message;
    } else {
      other.push(issue.message);
    }
  }
  return { fieldErrors, error: other.length > 0 ? other.join(" ") : null };
}

/** New record group (names only). Mount it only while open. */
export function CreateGroupDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(createGroupReducer, initialCreateGroupState);
  const createMutation = useCreateCrmMaster();
  const isCreating = createMutation.isLoading;

  const handleCreate = async (): Promise<void> => {
    const parsed = CreateCrmMasterRequestValidation.safeParse({
      long_name: state.longName,
      // A blank short name sends the name.
      short_name: state.shortName.trim() || state.longName,
    });
    if (!parsed.success) {
      dispatch({ type: "createFailed", ...toNameErrors(parsed.error.issues) });
      return;
    }

    let result: Awaited<ReturnType<typeof createMutation.mutateAsync>>;
    try {
      result = await createMutation.mutateAsync(parsed.data);
    } catch (error: unknown) {
      dispatch({
        type: "createFailed",
        fieldErrors: {},
        error: error instanceof Error ? error.message : "The record group couldn't be created. Try again.",
      });
      return;
    }
    if (!result.success) {
      const issues = crmValidationIssues(result.data);
      dispatch({
        type: "createFailed",
        ...(issues
          ? toNameErrors(issues)
          : {
            fieldErrors: {},
            error: isCrmAuthError(result)
              ? "You don't have permission to make this change."
              : result.message || "The record group couldn't be created. Try again.",
          }),
      });
      return;
    }
    onClose();
    navigate(generatePath(RoutePath.CrmGroup, { masterId: result.data.id }));
  };

  return (
    <Dialog open onOpenChange={(nextOpen: boolean) => { if (!nextOpen && !isCreating) onClose(); }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>New record group</DialogTitle>
          <DialogDescription>Add records to it from its page once it's created.</DialogDescription>
        </DialogHeader>

        <form
          className="grid gap-5 py-2"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void handleCreate();
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="crm-create-group-long-name">Name *</Label>
            <Input
              id="crm-create-group-long-name"
              value={state.longName}
              onChange={(event) => dispatch({ type: "changeLongName", longName: event.target.value })}
              disabled={isCreating}
              aria-invalid={Boolean(state.fieldErrors.long_name)}
              data-testid="crm-create-group-long-name"
            />
            {state.fieldErrors.long_name && <p className="text-sm font-medium text-destructive">{state.fieldErrors.long_name}</p>}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="crm-create-group-short-name">Short name</Label>
            <Input
              id="crm-create-group-short-name"
              value={state.shortName}
              placeholder="Same as name"
              onChange={(event) => dispatch({ type: "changeShortName", shortName: event.target.value })}
              disabled={isCreating}
              aria-invalid={Boolean(state.fieldErrors.short_name)}
              data-testid="crm-create-group-short-name"
            />
            {state.fieldErrors.short_name && <p className="text-sm font-medium text-destructive">{state.fieldErrors.short_name}</p>}
          </div>

          {state.error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="crm-create-group-error">
              {state.error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isCreating}>Cancel</Button>
            <Button type="submit" disabled={isCreating} data-testid="crm-create-group-submit">
              {isCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              {isCreating ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
